import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useAcceptAllFrameQuery,
  useFrameSubmitQuery,
  useVerfiyModal,
} from '@/hook';
import useRejectModal from '@/hook/frame/useRejectModal';
import {
  ArrowBack,
  ArrowForward,
  LeftPanelClose,
  Send,
  Undo,
  VButton,
  VFlex,
  VStack,
  VTextButton,
  VTextarea,
  VTooltip,
  Verified,
  useVModal,
} from '@/package';
import {
  useReviewStore,
  useTaskFrameStore,
  useToolMenuStore,
  useToolPermissionStore,
} from '@/store';

import { useToolPlayControllerContext } from './ToolPlayController';
import { ToolRejectReasonBoxViewer } from './ToolRejectReasonBoxViewer';

export const TempToolReviewCommentFormViewer = () => {
  const { t } = useTranslation();
  const { openConfirm } = useVModal();
  const currentFrame = useTaskFrameStore(state => state.currentFrame);
  const permission = useToolPermissionStore(state => state.permission);
  const comment = useReviewStore(state => state.comment);
  const { $toolPlayController, moveFrameSeq } = useToolPlayControllerContext();
  const { mutate: submit, isPending: isSubmitPending } = useFrameSubmitQuery();
  const { mutate: acceptAllFrame, isPending: isAcceptAllFramePending } =
    useAcceptAllFrameQuery();
  const { setReviewCommentVisible } = useReviewStore();
  const [errorMessage, setErrorMessage] = useState('');
  const frameSeq = useMemo(
    () => currentFrame?.frameSeq ?? -1,
    [currentFrame?.frameSeq],
  );
  const taskID = useMemo(
    () => +(currentFrame?.taskID ?? -1),
    [currentFrame?.taskID],
  );
  const { openVerifyModal } = useVerfiyModal({
    frameSeq,
    taskId: taskID,
  });
  const { openRejectModal } = useRejectModal({
    frameSeq,
    taskId: taskID,
    onSuccess: () => {
      setErrorMessage('');
    },
  });

  const submitFrame = () => {
    submit({
      pTaskID: taskID,
      pFrameSeq: frameSeq,
    });
  };

  const acceptAll = () => {
    acceptAllFrame({
      pTaskID: taskID,
    });
  };

  const onSubmitClick = () => {
    openConfirm({
      title: t('task.submitAction.hyperresonant'),
      message: () => (
        <VStack className="w-full" gap={0.25}>
          <div className="text-body-xl">
            {t('task.submitCurrentFrame.rosieresite')}
          </div>
          <div className="text-body-lg text-gray-600">
            {t('task.submitCurrentFrameDescription.lymphangioma')}
          </div>
        </VStack>
      ),
      cancelText: t('modal.cancelButtonText.intimidate'),
      confirmText: t('task.submitAction.hyperresonant'),
      onConfirm: () => submitFrame(),
    });
  };

  const onAcceptAllFrameClick = () => {
    openConfirm({
      title: t('review.acceptAllButton.radiotropism'),
      message: () => (
        <VStack className="w-full" gap={0.25}>
          <div className="text-body-xl">
            {t('review.acceptAllFrame.splanchnoblast')}
          </div>
          <div className="text-body-lg text-gray-600">
            {t('review.acceptAllFrameDescription.pomster')}
          </div>
        </VStack>
      ),
      cancelText: t('modal.cancelButtonText.intimidate'),
      confirmText: t('review.acceptAllButton.radiotropism'),
      onConfirm: () => acceptAll(),
    });
  };

  const onNextReviewClick = () =>
    currentFrame?.nextReviewFrame &&
    moveFrameSeq(currentFrame.nextReviewFrame.frameSeq);

  const onPrevReviewClick = () =>
    currentFrame?.prevReviewFrame &&
    moveFrameSeq(currentFrame.prevReviewFrame.frameSeq);

  const onNextRejectedClick = () =>
    currentFrame?.nextRejectedFrame &&
    moveFrameSeq(currentFrame.nextRejectedFrame.frameSeq);

  const onPrevRejectedClick = () =>
    currentFrame?.prevRejectedFrame &&
    moveFrameSeq(currentFrame.prevRejectedFrame.frameSeq);

  return (
    <>
      <VStack
        className={
          'absolute top-0 left-0 w-[314px] overflow-hidden bg-white p-2 shadow-sm'
        }
        style={{
          bottom: `${$toolPlayController.current?.offsetHeight}px`,
        }}
        gap={0.5}
      >
        <VStack className="relative size-full overflow-hidden">
          <VStack className="w-full border-b border-gray-200 pb-2" gap={0.5}>
            <VFlex justify="between" items="center">
              <div className="text-body-lg font-bold text-blue-300">
                {t('tool.leftSideViewerTab.decorate')}
              </div>

              {permission.labelingReview.updatable && (
                <VFlex
                  gap={0.25}
                  className="rounded-[2px] bg-status-inReview/50 p-1"
                  items="center"
                >
                  <div className="size-2 rounded-full bg-status-inReview" />
                  <div className="text-body-md">
                    {t('tool.leftSideViewerTab.nonsubscriber')}
                  </div>
                </VFlex>
              )}
            </VFlex>

            <VTextarea
              autoFocus
              content={errorMessage}
              fluid
              readOnly={!permission.labelingReview.updatable}
              rows={8}
              value={comment}
              valid={!errorMessage.length}
              onChange={e =>
                useReviewStore.getState().setComment(e.target.value)
              }
            />

            {permission.labelingReview.updatable && (
              <VFlex gap={0.5} items="center">
                <VButton
                  size="md"
                  variant="dangerOutline"
                  fluid
                  onClick={() => {
                    if (!comment.length) {
                      setErrorMessage(t('review.frameAprrove.scrimpiness'));
                      return;
                    }

                    openRejectModal();
                  }}
                >
                  <VFlex gap={0.25} items="center">
                    <Undo className="size-4" />
                    {t('review.rejectButtonText.pioneership')}
                  </VFlex>
                </VButton>
                <VButton size="md" fluid onClick={openVerifyModal}>
                  <VFlex gap={0.25} items="center">
                    <Verified className="size-4" />
                    {t('review.accpetButtonText.predisplacement')}
                  </VFlex>
                </VButton>
              </VFlex>
            )}

            {permission.labelingReview.readable && (
              <>
                <VFlex className="w-full px-px" justify="between">
                  {/* TODO: 툴팁 render 수정 후 해당 코드 수정 */}
                  <VTooltip
                    content={
                      "Only available for frames in 'submitted' or 'in review' status"
                    }
                  >
                    <div>
                      <VTextButton
                        size="sm"
                        onClick={onPrevReviewClick}
                        disabled={!currentFrame?.prevReviewFrame}
                      >
                        <VFlex gap={0.25} items="center">
                          <ArrowBack className="size-[14px]" />
                          Prev
                        </VFlex>
                      </VTextButton>
                    </div>
                  </VTooltip>

                  <VTooltip
                    content={
                      "Only available for frames in 'submitted' or 'in review' status"
                    }
                  >
                    <div>
                      <VTextButton
                        size="sm"
                        onClick={onNextReviewClick}
                        disabled={!currentFrame?.nextReviewFrame}
                      >
                        <VFlex gap={0.25} items="center">
                          <ArrowForward className="size-[14px]" />
                          Next
                        </VFlex>
                      </VTextButton>
                    </div>
                  </VTooltip>
                </VFlex>
                <VFlex className="w-full px-px" justify="between">
                  {/* TODO: 툴팁 render 수정 후 해당 코드 수정 */}
                  <VTooltip
                    content={
                      "Only available for frames in 'submitted' or 'in review' status"
                    }
                  >
                    <div>
                      <VTextButton
                        size="sm"
                        onClick={onPrevRejectedClick}
                        disabled={!currentFrame?.prevRejectedFrame}
                      >
                        <VFlex gap={0.25} items="center">
                          <ArrowBack className="size-[14px]" />
                          Prev Rejected
                        </VFlex>
                      </VTextButton>
                    </div>
                  </VTooltip>

                  <VTooltip
                    content={
                      "Only available for frames in 'submitted' or 'in review' status"
                    }
                  >
                    <div>
                      <VTextButton
                        size="sm"
                        onClick={onNextRejectedClick}
                        disabled={!currentFrame?.nextRejectedFrame}
                      >
                        <VFlex gap={0.25} items="center">
                          <ArrowForward className="size-[14px]" />
                          Next Rejected
                        </VFlex>
                      </VTextButton>
                    </div>
                  </VTooltip>
                </VFlex>
              </>
            )}
          </VStack>

          <ToolRejectReasonBoxViewer />

          <VStack gap={0.5} className="w-full">
            {permission.labelingReview.acceptable &&
              currentFrame?.workState === 'Reviewing' && (
                <VButton
                  fluid
                  loading={isAcceptAllFramePending}
                  outlined
                  size="md"
                  variant="secondary"
                  onClick={onAcceptAllFrameClick}
                >
                  {t('review.allVerifyButtonText.elderwood')}
                </VButton>
              )}

            {permission.labeling.updatable &&
              currentFrame?.workState === 'Working' && (
                <VButton
                  fluid
                  loading={isSubmitPending}
                  outlined
                  size="md"
                  variant="secondary"
                  onClick={onSubmitClick}
                >
                  <Send />
                  {t('task.submitAction.hyperresonant')}
                </VButton>
              )}
          </VStack>
        </VStack>
      </VStack>

      <VTooltip content={t('review.pannelCloseTooltip.pledgeless')}>
        <div
          className="absolute top-0 left-[314px] rounded-r-sm bg-white p-1"
          role="button"
          onClick={() => {
            setReviewCommentVisible(false);
            useToolMenuStore
              .getState()
              .deselect(
                useToolMenuStore.getState().findByName('reviewData')!.id,
              );
          }}
        >
          <LeftPanelClose className="size-4" />
        </div>
      </VTooltip>
    </>
  );
};
